/**
 * Vođene browsing rules (A2): displayed minutes, duration buckets, search and rows.
 *
 * Buckets use the minutes the card shows (floored), so a session labelled
 * "5 min" is always SOS (Q21; readiness §5).
 */
import type { Language, SessionGroup } from '@/types';
import { toIntlLocale } from './locale';

export type DurationBucket = 'sos' | 'standard' | 'deep';
export type DurationFilter = 'all' | DurationBucket;

export const DURATION_FILTERS: readonly DurationFilter[] = ['all', 'sos', 'standard', 'deep'];

/** SOS: at most this many displayed minutes. */
const SOS_MAX_MINUTES = 5;
/** Produbljene: at least this many displayed minutes. */
const DEEP_MIN_MINUTES = 19;

/** Whole minutes, as shown on a card. */
export function displayedMinutes(seconds: number): number {
  return Math.floor(seconds / 60);
}

/** "12 min", or "1h 5m" from an hour. */
export function formatSessionDuration(seconds: number): string {
  const minutes = displayedMinutes(seconds);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  return `${minutes} min`;
}

/** "1:23": a playback position as the player shows it. */
export function formatPlaybackTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, '0')}`;
}

/** SOS ≤ 5 min, Standardne 6–18 min, Produbljene ≥ 19 min. */
export function durationBucket(seconds: number): DurationBucket {
  const minutes = displayedMinutes(seconds);
  if (minutes <= SOS_MAX_MINUTES) return 'sos';
  if (minutes >= DEEP_MIN_MINUTES) return 'deep';
  return 'standard';
}

export function matchesDuration(seconds: number, filter: DurationFilter): boolean {
  return filter === 'all' || durationBucket(seconds) === filter;
}

interface Searchable {
  title: string;
  description: string | null;
}

/** Case-insensitive match of the query in the title, the description or the group name. */
export function matchesSearch(
  session: Searchable,
  groupName: string | undefined,
  query: string,
  language: Language,
): boolean {
  const locale = toIntlLocale(language);
  const needle = query.trim().toLocaleLowerCase(locale);
  if (!needle) return true;
  return [session.title, session.description, groupName].some(
    (field) => field?.toLocaleLowerCase(locale).includes(needle) ?? false,
  );
}

export interface SessionRow<T> {
  group: SessionGroup;
  sessions: T[];
}

/**
 * Key of the last row, for published sessions with no group or a group missing from
 * /groups (a seed script, or a group deleted by a later data migration: ON DELETE SET NULL).
 * Real group keys are snake_case words, so the leading underscore cannot clash.
 */
export const UNGROUPED_GROUP_KEY = '_ungrouped';

interface Groupable {
  id: string;
  duration: number;
  group_key?: string | null;
}

/** Shortest first, then by id: the order inside a row and in a category. */
function byDurationThenId(a: Groupable, b: Groupable): number {
  return a.duration - b.duration || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * One row per group with sessions, in position order, each ordered by duration, then id.
 * Sessions without a known group go into a last `fallback` row, so no session goes missing.
 */
export function groupSessions<T extends Groupable>(
  sessions: readonly T[],
  groups: readonly SessionGroup[],
  fallback: SessionGroup,
): SessionRow<T>[] {
  const known = new Set(groups.map((group) => group.key));
  const ordered = [...sessions].sort(byDurationThenId);
  const rows = [...groups]
    .sort((a, b) => a.position - b.position)
    .map((group) => ({ group, sessions: ordered.filter((session) => session.group_key === group.key) }));
  rows.push({ group: fallback, sessions: ordered.filter((session) => !known.has(session.group_key ?? '')) });
  return rows.filter((row) => row.sessions.length > 0);
}
